// lib/problemBankApi.ts
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Import supabase client for direct queries
import { supabase } from './supabaseClient';

// Use your existing Edge Function
const FUNCTION_NAME = 'hyper-endpoint';

export const problemBankApi = {
  // ==============================================
  // PROBLEM BANK OPERATIONS
  // ==============================================

  // Get all problems
  getProblems: async (filters?: any) => {
    try {
      let params = '';
      if (filters) {
        const searchParams = new URLSearchParams();
        if (filters.status) searchParams.append('status', filters.status);
        if (filters.priority) searchParams.append('priority', filters.priority);
        if (filters.minLoss) searchParams.append('minLoss', filters.minLoss);
        params = `?${searchParams.toString()}`;
      }
      
      const functionUrl = `${supabaseUrl}/functions/v1/${FUNCTION_NAME}/problems${params}`;
      
      console.log('📡 Fetching problems from:', functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('✅ Received problems:', data?.length || 0);
      return data || [];
      
    } catch (error) {
      console.error('Error fetching problems:', error);
      // Fallback to direct Supabase query if Edge Function fails
      console.log('⚠️ Falling back to direct Supabase query...');
      return fallbackGetProblems(filters);
    }
  },

  // Get single problem
  getProblem: async (id: string) => {
    try {
      const functionUrl = `${supabaseUrl}/functions/v1/${FUNCTION_NAME}/problems/${id}`;
      
      console.log('📡 Fetching problem from:', functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.error('Error fetching problem:', error);
      return fallbackGetProblem(id);
    }
  },

  // ==============================================
  // EXTERNAL SUBMISSIONS - FIXED TO USE DIRECT SUPABASE
  // ==============================================

  // Get external submissions - DIRECT DATABASE QUERY
  getExternalSubmissions: async (status?: string) => {
    try {
      console.log('🔍 Fetching external submissions directly from database...');
      
      let query = supabase
        .from('external_submissions')
        .select('*')
        .order('submitted_at', { ascending: false });
      
      if (status && status !== 'all') {
        query = query.eq('status', status);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('❌ Database error:', error);
        throw error;
      }
      
      console.log(`✅ Loaded ${data?.length || 0} submissions from database`);
      
      // Log the first submission to see what we got
      if (data && data.length > 0) {
        console.log('📊 First submission:', {
          id: data[0].id,
          company_name: data[0].company_name,
          problem_title: data[0].problem_title,
          status: data[0].status
        });
      }
      
      // Transform to match your frontend types
      return (data || []).map(sub => ({
        id: sub.id,
        companyName: sub.company_name,
        companyRegistration: sub.company_registration,
        contactName: sub.contact_name,
        contactTitle: sub.contact_title,
        contactEmail: sub.contact_email,
        contactPhone: sub.contact_phone,
        problemTitle: sub.problem_title,
        problemDescription: sub.problem_description,
        estimatedImpact: sub.estimated_impact,
        urgency: sub.urgency,
        documents: sub.documents || [],
        status: sub.status,
        submittedAt: sub.submitted_at,
        reviewedBy: sub.reviewed_by,
        reviewedAt: sub.reviewed_at,
        reviewNotes: sub.review_notes,
        convertedToProblemId: sub.converted_to_problem_id
      }));
      
    } catch (error) {
      console.error('Error fetching submissions:', error);
      return [];
    }
  },

  // Submit external problem - DIRECT DATABASE INSERT
  submitExternalProblem: async (submission: any) => {
    try {
      console.log('📝 Submitting problem directly to database...');
      
      // Map the submission data to match your database schema
      const dbSubmission = {
        company_name: submission.companyName || 'Individual Submission',
        company_registration: submission.companyRegistration || null,
        contact_name: submission.contactName,
        contact_title: submission.contactTitle || 'Individual',
        contact_email: submission.contactEmail,
        contact_phone: submission.contactPhone,
        problem_title: submission.problemTitle,
        problem_description: submission.problemDescription,
        estimated_impact: submission.estimatedImpact,
        urgency: submission.urgency,
        documents: submission.documents || [],
        status: 'pending',
        submitted_at: new Date().toISOString()
      };
      
      console.log('📊 Inserting:', dbSubmission);
      
      const { data, error } = await supabase
        .from('external_submissions')
        .insert(dbSubmission)
        .select()
        .single();
      
      if (error) {
        console.error('❌ Database error:', error);
        throw error;
      }
      
      console.log('✅ Submission successful:', data.id);
      return data;
      
    } catch (error) {
      console.error('Error submitting problem:', error);
      throw error;
    }
  },

  // Review submission - DIRECT DATABASE UPDATE
  reviewSubmission: async (submissionId: string, status: 'accepted' | 'rejected', notes: string, convertedToProblemId?: string) => {
    try {
      console.log(`📝 Reviewing submission ${submissionId} as ${status}...`);
      
      const { data, error } = await supabase
        .from('external_submissions')
        .update({
          status: status,
          review_notes: notes,
          reviewed_at: new Date().toISOString(),
          converted_to_problem_id: convertedToProblemId
        })
        .eq('id', submissionId)
        .select()
        .single();
      
      if (error) {
        console.error('❌ Database error:', error);
        throw error;
      }
      
      console.log('✅ Review successful:', data);
      return data;
      
    } catch (error) {
      console.error('Error reviewing submission:', error);
      throw error;
    }
  },

  // Get single submission
  getSubmission: async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('external_submissions')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
      
    } catch (error) {
      console.error('Error fetching submission:', error);
      return null;
    }
  },

  // Create new problem
  createProblem: async (problem: any) => {
    try {
      const functionUrl = `${supabaseUrl}/functions/v1/${FUNCTION_NAME}/problems`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(problem),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.error('Error creating problem:', error);
      throw error;
    }
  },

  // Update problem
  updateProblem: async (id: string, updates: any) => {
    try {
      const functionUrl = `${supabaseUrl}/functions/v1/${FUNCTION_NAME}/problems/${id}`;
      
      const response = await fetch(functionUrl, {
        method: 'PATCH',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.error('Error updating problem:', error);
      throw error;
    }
  },

  // Calculate priority score
  calculatePriorityScore: async (id: string) => {
    try {
      const functionUrl = `${supabaseUrl}/functions/v1/${FUNCTION_NAME}/problems/${id}/score`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.error('Error calculating score:', error);
      throw error;
    }
  },

  // Express interest
  expressInterest: async (problemId: string, squadId: string, message: string) => {
    try {
      const functionUrl = `${supabaseUrl}/functions/v1/${FUNCTION_NAME}/problems/${problemId}/interest`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ squadId, message }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.error('Error expressing interest:', error);
      throw error;
    }
  },

  // Convert submission to problem
  convertSubmissionToProblem: async (submissionId: string, problemData: any) => {
    try {
      const problem = await problemBankApi.createProblem(problemData);
      await problemBankApi.reviewSubmission(submissionId, 'accepted', 'Converted to problem bank entry', problem.id);
      return problem;
    } catch (error) {
      console.error('Error converting submission:', error);
      throw error;
    }
  },

  // Get analytics
  getAnalytics: async () => {
    try {
      const functionUrl = `${supabaseUrl}/functions/v1/${FUNCTION_NAME}/analytics`;
      
      const response = await fetch(functionUrl, {
        method: 'GET',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return null;
    }
  },

  // Upload documents
  uploadDocuments: async (submissionId: string, formData: FormData) => {
    try {
      const functionUrl = `${supabaseUrl}/functions/v1/${FUNCTION_NAME}/submissions/${submissionId}/documents`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      return await response.json();
      
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw error;
    }
  }
};

// ==============================================
// FALLBACK FUNCTIONS (if Edge Function fails)
// ==============================================

async function fallbackGetProblems(filters?: any) {
  try {
    let query = supabase
      .from('problem_bank')
      .select('*')
      .order('created_at', { ascending: false });
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return (data || []).map(problem => ({
      ...problem,
      problemId: problem.problem_id,
      anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
      quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
      priorityScore: problem.priority_score,
    }));
    
  } catch (error) {
    console.error('Fallback also failed:', error);
    return [];
  }
}

async function fallbackGetProblem(id: string) {
  try {
    const { data, error } = await supabase
      .from('problem_bank')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    
    if (!data) return null;
    
    return {
      ...data,
      problemId: data.problem_id,
      anchor: typeof data.anchor === 'string' ? JSON.parse(data.anchor) : data.anchor,
      quantification: typeof data.quantification === 'string' ? JSON.parse(data.quantification) : data.quantification,
      priorityScore: data.priority_score,
    };
    
  } catch (error) {
    console.error('Fallback failed:', error);
    return null;
  }
}